import AccountSnapshot from '../../components/AccountSnapshot';
import { startWindowDisabledTime } from '../../utils/startWindow';
import { isTerminalAccount, TERMINAL_START_MESSAGE } from '../../utils/terminalAccount';
import DesktopAppLink, { DESKTOP_APP_PATH } from '../../components/DesktopAppLink';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { App, Tabs, Form, Select, Button, Modal, TimePicker, Switch, Input, Spin } from 'antd';
import {
  ClockCircleOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  CaretDownOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import apiService from '../../services/api';
import { ServiceType } from '../../services/api';
import { useExecutionStatus, isBotUp } from '../../hooks/useExecutionStatus';
import { FutureInstrument, PublishedStrategy } from '../../types';
import { ChartPriceLine } from '../../components/CandlestickChart';
import { useWebSocketPriceData } from '../../hooks/useWebSocketPriceData';
import { usePortfolios } from '../../hooks/usePortfolios';
import OrderAndTransactionsTab from '../PairTrading/tabs/OrderAndTransactionsTab';
import PortfolioTab from '../PairTrading/tabs/PortfolioTab';
import ChartSection from './tabs/ChartSection';
import ConfigurationSection, { ConfigActionBar, MobileConfigSection } from './tabs/ConfigurationSection';
import LogTab from './tabs/LogTab';
import CapitalHistoryTab from './tabs/CapitalHistoryTab';
import GridPerformanceTab from './tabs/GridPerformanceTab';
import BasicHoldings from '../../components/BasicHoldings';
import InstrumentCapitalPanel, { InstrumentCapitalPanelHandle } from './components/InstrumentCapitalPanel';
import BidAsk from '../../components/BidAsk';
import TradingPageHeader from '../../components/TradingPageHeader';
import OnboardingTour from '../../components/OnboardingTour';
import { GRID_ONBOARDING_KEY, ONBOARDING_DEMO_SYMBOL, gridOnboardingSteps } from './onboardingSteps';
import CorporateActionBadge from '../../components/CorporateActionBadge';
import FirstAllocationModal, { AllocationPlan } from './components/FirstAllocationModal';
import ImportInProgressModal from '../../components/ImportJobProgress/ImportInProgressModal';
import { ImportInProgress, importInProgressOf } from '../../services/importJobApi';
import { ChartConfigSplit, ChartTabsSplit } from '../../components/ResponsiveTradingLayout';
import { MobileTradingShell, ShellSection, UnsavedDot } from '../../components/mobile';
import useIsMobile from '../../hooks/useIsMobile';
import { CloseOutlined, PauseCircleOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { formatNumber, formatInt, formatDecimal, formatAbbrev } from '../../utils/numberFormat';


const coreService = apiService.getService(ServiceType.CORE);

/** While the bot runs, fills move positions, orders and balances: re-read them this often. */
const LIVE_POLL_MS = 15_000;

/** Pills served by the single ConfigurationSection instance rather than their own node. */
const CONFIG_SECTIONS: MobileConfigSection[] = ['settings', 'levels', 'advanced'];


interface VolumePriceRange {
  key: string;
  minPrice: number;
  maxPrice: number;
  volume: number;
}

interface GridTradingFormValues {
  allow_to_exceed_floor_ceiling: boolean;
  grid_interval: number;
  volume_by_price_range: number[][];
  grid_type: 'BULL' | 'BEAR';
  grid_origin: number;
  grid_height: number;
  capacity: number;
  stop_loss_quantity: number;
  stop_operating_threshold: number;
  forced_close: boolean;
}

type ConfigDataMap = Record<string, Record<string, any>>;

const GridTrading: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm<GridTradingFormValues>();

  // Watch grid params to compute chart price lines
  const gridOrigin = Form.useWatch('grid_origin', form);
  const gridInterval = Form.useWatch('grid_interval', form);
  const gridHeight = Form.useWatch('grid_height', form);

  const strategyIdFromRoute = useMemo(() => (location.state as any)?.strategyId || null, [location.state]);
  const executionIdFromRoute = useMemo(() => (location.state as any)?.executionId || null, [location.state]);

  const [gridStrategyId, setGridStrategyId] = useState<string | null>(null);
  const [gridExecutionId, setGridExecutionId] = useState<string | null>(null);
  const strategyId = strategyIdFromRoute || gridStrategyId;
  const executionId = executionIdFromRoute || gridExecutionId;

  useEffect(() => {
    if (strategyIdFromRoute) return;
    const fetchGridStrategy = async () => {
      try {
        const res = await coreService.get<{ data?: PublishedStrategy[] }>('/strategies/published', { params: { type: 'GRID' } });
        const list = (res.data && (res.data as any).data) ? (res.data as any).data : (Array.isArray(res.data) ? res.data : []);
        setStrategies(list);
        const grid = list.find((s: PublishedStrategy) => (s.type && (s.type === 'GRID' || s.type === 'GRID_TRADING')));
        if (grid?.id) setGridStrategyId(grid.id);
      } catch (e) {
        console.error('Failed to fetch grid strategy for logs', e);
      }
    };
    fetchGridStrategy();
  }, [strategyIdFromRoute]);

  const [activeTab, setActiveTab] = useState('1');
  const [tabRefreshKey, setTabRefreshKey] = useState(0);
  const [bottomTabKey, setBottomTabKey] = useState('logs');
  // Responsive: below 992px the config column moves into a bottom Drawer.
  const isMobile = useIsMobile(992);
  const [mobileConfigOpen, setMobileConfigOpen] = useState(false);
  /** Which pill the compact shell is showing. Config groups share one form instance. */
  const [mobileSection, setMobileSection] = useState<string>('settings');
  const [logsHighlight, setLogsHighlight] = useState(false);
  const [loading, setLoading] = useState(false);
  const [volumeRanges, setVolumeRanges] = useState<VolumePriceRange[]>([]);
  const [rangesManuallyEdited, setRangesManuallyEdited] = useState(false);
  const [priceLevels, setPriceLevels] = useState<{ price: number; quantity: number }[]>([]);

  // Grid config state — one config per portfolio, configData is a map of symbol→params
  const [configId, setConfigId] = useState<string | null>(null);
  const [fullConfigData, setFullConfigData] = useState<ConfigDataMap>({});
  const [configsLoading, setConfigsLoading] = useState(false);
  const [currentNav, setCurrentNav] = useState<number | null>(null);
  const [purchasingPower, setPurchasingPower] = useState<number | null>(null);

  // View state: null = no stock selected, string = editing that symbol
  const [editingSymbol, setEditingSymbol] = useState<string | null>(null);
  const [unsavedSymbols, setUnsavedSymbols] = useState<Set<string>>(new Set());
  const [formDirty, setFormDirty] = useState(false);
  const formDirtyRef = useRef(false);
  const editingSymbolRef = useRef<string | null>(null);
  // Monotonic token: only the newest doNavigateToStock call may write state.
  const navSeqRef = useRef(0);
  const saveInFlightRef = useRef(false);
  const placeInFlightRef = useRef(false);
  const stopInFlightRef = useRef(false);

  // Search and selection state
  const [selectedInstrument, setSelectedInstrument] = useState<FutureInstrument | null>(null);
  const [futureSearchResults, setFutureSearchResults] = useState<FutureInstrument[]>([]);
  const [futureSearchLoading, setFutureSearchLoading] = useState(false);
  const [latestPrice, setLatestPrice] = useState<number | null>(null);
  const [latestChangePercent, setLatestChangePercent] = useState<number | null>(null);
  // "+ Add instrument" modal — dedicated search screen so the user doesn't have to
  // discover the search field hidden inside the trading-parameter panel.
  const [addInstrumentOpen, setAddInstrumentOpen] = useState(false);
  const [addInstrumentQuery, setAddInstrumentQuery] = useState('');
  // The onboarding tour types a sample symbol here; picking a result is off while it does.
  const [addInstrumentDemo, setAddInstrumentDemo] = useState(false);

  const {
    portfolios,
    selectedPortfolioId,
    setSelectedPortfolioId,
    selectedPortfolio,
    loading: loadingPortfolios
  } = usePortfolios({ accountType: 'EQUITY' });

  // The bot's phase comes from the server (today's StrategyExecution), not from what this tab
  // last clicked: a schedule start, a crash, a reload or a minutes-long STOPPING all change it.
  const { phase: botPhase, refresh: refreshBotPhase } = useExecutionStatus({
    portfolioId: selectedPortfolioId,
    strategyType: 'GRID',
  });
  const isBotRunning = isBotUp(botPhase);
  // Symbols of the running bot paused by a per-symbol Stop. Only Core can see the flag
  // (it lives in the engine's Redis config), so it is fetched, not derived.
  const [pausedSymbols, setPausedSymbols] = useState<Set<string>>(new Set());

  const [expandedSymbols, setExpandedSymbols] = useState<Set<string>>(new Set());

  const toggleSymbolExpansion = (symbol: string) => {
    setExpandedSymbols(prev => {
      const newSet = new Set(prev);
      if (newSet.has(symbol)) {
        newSet.delete(symbol);
      } else {
        newSet.add(symbol);
      }
      return newSet;
    });
  };

  const { modal, message } = App.useApp();
  const modalRef = useRef(modal);
  useEffect(() => { modalRef.current = modal; }, [modal]);
  const futureSearchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const capitalPanelRef = useRef<InstrumentCapitalPanelHandle>(null);

  // Derived: entries from fullConfigData
  const stockEntries = useMemo(() => {
    return Object.entries(fullConfigData).map(([symbol, config]) => ({ symbol, config }));
  }, [fullConfigData]);

  // Compute grid price lines from form values
  const gridPriceLines = useMemo<ChartPriceLine[]>(() => {
    const origin = Number(gridOrigin);
    const interval = Number(gridInterval);
    const height = Number(gridHeight);
    if (!origin || !interval || !height || isNaN(origin) || isNaN(interval) || isNaN(height)) return [];

    const lines: ChartPriceLine[] = [];
    for (let i = 0; i <= height; i++) {
      const price = origin - i * interval;
      if (price <= 0) break;
      lines.push({
        price,
        color: i === 0 ? '#0ECB81' : i === height ? '#F6465D' : '#848E9C',
        lineWidth: (i === 0 || i === height) ? 2 : 1,
        lineStyle: 2, // Dashed
        title: i === 0 ? 'Upper' : i === height ? 'Lower' : `L${i}`,
      });
    }
    return lines;
  }, [gridOrigin, gridInterval, gridHeight]);

  // Search stocks
  const handleStockSearch = useCallback((value: string) => {
    if (futureSearchTimerRef.current) clearTimeout(futureSearchTimerRef.current);
    if (!value || value.trim().length === 0) {
      setFutureSearchResults([]);
      return;
    }
    setFutureSearchLoading(true);
    futureSearchTimerRef.current = setTimeout(async () => {
      try {
        const response = await coreService.get(`/search/stocks?q=${encodeURIComponent(value)}`);
        const results: FutureInstrument[] = response.data.results.map((item: any) => ({
          symbol: item.symbol,
          name: item.symbol,
          price: item.price || 0,
          volume: 0,
          openInterest: 0,
          expiryDate: '',
          change24h: item.latestChange || 0,
          changePercent24h: item.changePercent || 0
        }));
        setFutureSearchResults(results);
      } catch (error: any) {
        console.error('Error searching stocks:', error);
        setFutureSearchResults([]);
      } finally {
        setFutureSearchLoading(false);
      }
    }, 300);
  }, []);

  // Position data for current symbol
  const [positionData, setPositionData] = useState<Record<string, any> | null>(null);
  const [loadingPositions, setLoadingPositions] = useState(false);

  // Find position entry for a symbol, handling exchange-prefixed keys (e.g. "HSX:VIB" matches "VIB")
  const findSymbolPosition = useCallback((data: Record<string, any>, symbol: string) => {
    if (!data || !symbol) return null;
    if (data[symbol] != null) return data[symbol];
    const entry = Object.entries(data).find(([key]) => key === symbol || key.endsWith(`:${symbol}`));
    return entry ? entry[1] : null;
  }, []);

  // Internal navigate — always executes without confirmation
  const doNavigateToStock = useCallback(async (symbol: string) => {
    // This function awaits a price fetch and writes state on both sides of the await, so two
    // overlapping calls used to interleave: one won `editingSymbol`, another won the form and
    // the price levels, and the panel ended up showing a symbol from a different portfolio.
    // Only the newest navigation may write.
    const seq = ++navSeqRef.current;
    // Clear current instrument first so chart re-fetches even for the same symbol
    setSelectedInstrument(null);
    setEditingSymbol(symbol);
    setFormDirty(false);
    // Track unsaved symbol — only allow one unsaved symbol at a time (replace previous)
    if (!(symbol in fullConfigData)) {
      setUnsavedSymbols(new Set([symbol]));
    }

    try {
      const priceResponse = await coreService.get(`/price/latest/${symbol}`);
      const instrument: FutureInstrument = {
        symbol,
        name: symbol,
        price: (priceResponse.data.latest_matched_price || priceResponse.data.ref_price || priceResponse.data.price || 0),
        volume: 0,
        openInterest: 0,
        expiryDate: '',
        change24h: 0,
        changePercent24h: priceResponse.data.changePercent || priceResponse.data.ref_diff_pct || 0
      };
      if (seq !== navSeqRef.current) return;
      setSelectedInstrument(instrument);
      setLatestPrice(instrument.price);
      setLatestChangePercent(instrument.changePercent24h);
    } catch {
      if (seq !== navSeqRef.current) return;
      setSelectedInstrument({
        symbol, name: symbol, price: 0, volume: 0, openInterest: 0,
        expiryDate: '', change24h: 0, changePercent24h: 0
      });
      setLatestPrice(0);
      setLatestChangePercent(0);
    }

    // Load stock-specific config into form
    const stockConfig = fullConfigData[symbol];
    if (stockConfig) {
      form.setFieldsValue({
        grid_type: stockConfig.grid_type,
        grid_interval: stockConfig.grid_interval,
        grid_origin: stockConfig.grid_origin,
        grid_height: stockConfig.grid_height,
        stop_loss_quantity: stockConfig.stop_loss_quantity,
        capacity: stockConfig.capacity || 0,
        stop_operating_threshold: stockConfig.stop_operating_threshold === Infinity ? null : stockConfig.stop_operating_threshold,
        allow_to_exceed_floor_ceiling: stockConfig.allow_to_exceed_floor_ceiling,
        forced_close: stockConfig.forced_close,
      });
      if (Array.isArray(stockConfig.volume_by_price_range)) {
        setVolumeRanges(convertToTableData(stockConfig.volume_by_price_range as number[][]));
        // Rebuild price levels from saved ranges (each range is [price, price, qty] for single-level entries)
        const levels = (stockConfig.volume_by_price_range as number[][])
          .filter(r => r.length >= 3)
          .map(r => ({ price: r[0], quantity: r[2] }));
        setPriceLevels(levels);
      } else {
        setVolumeRanges([]);
        setPriceLevels([]);
      }
      setRangesManuallyEdited(true);
      // Reset dirty after populating form (setFieldsValue triggers onValuesChange)
      setTimeout(() => setFormDirty(false), 0);
    } else {
      // Compute available purchasing power for new symbol (currentNav minus other symbols' capacity)
      const availableCapital = currentNav != null
        ? Math.floor(currentNav - Object.entries(fullConfigData)
            .filter(([sym]) => sym !== symbol)
            .reduce((sum, [, cfg]) => sum + (Number(cfg.capacity) || 0), 0))
        : undefined;
      form.setFieldsValue({
        grid_type: 'BULL',
        grid_height: undefined,
        allow_to_exceed_floor_ceiling: true,
        capacity: availableCapital != null && availableCapital > 0 ? availableCapital : undefined,
        grid_interval: undefined,
        grid_origin: undefined,
        stop_loss_quantity: 0,
        stop_operating_threshold: 1000,
        forced_close: false,
      });
      setVolumeRanges([]);
      setPriceLevels([]);
      setRangesManuallyEdited(false);
      setTimeout(() => setFormDirty(false), 0);
    }
  }, [fullConfigData, currentNav]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep refs in sync
  useEffect(() => { formDirtyRef.current = formDirty; }, [formDirty]);
  useEffect(() => { editingSymbolRef.current = editingSymbol; }, [editingSymbol]);

  // Public navigate — confirms if form has unsaved changes
  const navigateToStock = useCallback((symbol: string) => {
    if (symbol === editingSymbolRef.current) return;
    if (formDirtyRef.current) {
      modalRef.current.confirm({
        title: 'Unsaved Changes',
        content: `You have unsaved changes for ${editingSymbolRef.current}. Leave without saving?`,
        okText: 'Leave',
        cancelText: 'Stay',
        okButtonProps: { danger: true, style: { minWidth: '80px', fontWeight: 500, background: 'transparent', border: '1px solid #ff4d4f', color: '#ff4d4f' } },
        cancelButtonProps: { type: 'default', style: { background: '#2B3139', borderColor: '#3d4148', color: '#E0E0E0', minWidth: '80px', fontWeight: 500 } },
        onOk: () => doNavigateToStock(symbol),
      });
    } else {
      doNavigateToStock(symbol);
    }
  }, [doNavigateToStock]);

  const handleSelectStockFromSearch = useCallback((symbol: string) => {
    navigateToStock(symbol);
  }, [navigateToStock]);

  // Live data for chart/order book; header uses only initial last price (set in navigateToStock), not live stream, so it never flickers
  const { priceData: futurePriceData } = useWebSocketPriceData({
    symbol: selectedInstrument?.symbol || null,
    enabled: !!selectedInstrument?.symbol
  });

  // Converters
  const convertToTableData = (ranges: number[][]): VolumePriceRange[] =>
    ranges.map((range, index) => ({ key: `range-${index}`, minPrice: range[0], maxPrice: range[1], volume: range[2] || 0 }));

  const convertFromTableData = (ranges: VolumePriceRange[]): number[][] =>
    ranges.map(range => [range.minPrice, range.maxPrice, range.volume]);

  // Convert price levels to volume_by_price_range format for the backend
  const convertPriceLevelsToRanges = (levels: { price: number; quantity: number }[]): number[][] =>
    levels.filter(l => l.quantity > 0).map(l => [l.price, Math.round((l.price + 0.05) * 100) / 100, l.quantity]);

  const handleAddRange = () => {
    setRangesManuallyEdited(true);
    setVolumeRanges([...volumeRanges, { key: `range-${Date.now()}`, minPrice: 0, maxPrice: 0, volume: 0 }]);
  };

  const handleDeleteRange = (key: string) => {
    setRangesManuallyEdited(true);
    setVolumeRanges(volumeRanges.filter(range => range.key !== key));
  };

  const handleRangeChange = (key: string, field: keyof VolumePriceRange, value: number) => {
    setRangesManuallyEdited(true);
    setVolumeRanges(volumeRanges.map(range => range.key === key ? { ...range, [field]: value } : range));
  };

  const handleRangeUpdate = (key: string, payload: Partial<VolumePriceRange>) => {
    setRangesManuallyEdited(true);
    setVolumeRanges(prev => prev.map(range => range.key === key ? { ...range, ...payload } : range));
  };

  const handleSetRanges = (ranges: VolumePriceRange[]) => {
    setVolumeRanges(ranges);
  };

  // Fetch config for the selected portfolio by portfolio ID
  const fetchConfig = useCallback(async (portfolioId: string | null) => {
    if (!portfolioId) {
      setConfigId(null);
      setFullConfigData({});
      setCurrentNav(null);
      return;
    }
    try {
      setConfigsLoading(true);
      const [configResponse, navResponse] = await Promise.all([
        coreService.get(`/grid/portfolio/${portfolioId}/config`),
        coreService.get<{ currentNav: number }>('/performance/profit', { params: { portfolioId } }).catch(() => null),
      ]);

      const list = Array.isArray(configResponse.data) ? configResponse.data : configResponse.data ? [configResponse.data] : [];
      if (list.length > 0) {
        const first = list[0];
        setConfigId(first.id);
        const data = first.configData;
        setFullConfigData(data && typeof data === 'object' ? data : {});
      } else {
        setConfigId(null);
        setFullConfigData({});
      }

      setCurrentNav(navResponse?.data?.currentNav ?? null);
      // `purchasingPower` is now Σ currentCapital (cash allocated to this grid),
      // not broker pp. It's streamed up from <InstrumentCapitalPanel> via
      // onTotalCapitalChange — the panel already polls /instrument-config with
      // the aggregate we need, so no extra request here.
    } catch (err) {
      console.error('Failed to fetch grid config:', err);
      setConfigId(null);
      setFullConfigData({});
    } finally {
      setConfigsLoading(false);
    }
  }, []);

  useEffect(() => {
    setEditingSymbol(null);
    setSelectedInstrument(null);
    setUnsavedSymbols(new Set());
    form.resetFields();
    setVolumeRanges([]);
    setPriceLevels([]);
    fetchConfig(selectedPortfolioId);
  }, [selectedPortfolioId, fetchConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select the first symbol of the portfolio actually on screen.
  //
  // Gated on `selectedPortfolioId` because at mount it is '' — `fetchConfig('')` returns before
  // it can raise `configsLoading`, so this effect used to fire against an empty config and
  // navigate to a hardcoded ticker before any portfolio was known, racing the real one.
  //
  // The guard is "selected AND still reachable", not merely "selected": a symbol left over from
  // the previous portfolio has no tab in this one, and refusing to correct it stranded the
  // panel on a symbol the user could not even click away from.
  useEffect(() => {
    if (configsLoading) return;
    if (!selectedPortfolioId) return;
    const symbols = Object.keys(fullConfigData);
    if (symbols.length === 0) return; // nothing configured yet — "+ Add" is the way in
    if (editingSymbol && (editingSymbol in fullConfigData || unsavedSymbols.has(editingSymbol))) return;
    doNavigateToStock(symbols[0]);
  }, [configsLoading, fullConfigData, selectedPortfolioId, editingSymbol, unsavedSymbols]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save helper: get current config for portfolio, then PATCH if exists else POST (create)
  const persistConfigData = useCallback(async (newData: ConfigDataMap) => {
    if (!selectedPortfolioId) {
      throw new Error('No portfolio selected');
    }
    const response = await coreService.get(`/grid/portfolio/${selectedPortfolioId}/config`);
    const list = Array.isArray(response.data) ? response.data : response.data ? [response.data] : [];

    if (list.length > 0) {
      const existing = list[0];
      const id = existing.id;
      const existingData = (existing.configData && typeof existing.configData === 'object') ? existing.configData : {};
      const mergedData = { ...existingData, ...newData };
      // named version: a change made elsewhere between the read and this write is a 409, not lost
      await coreService.patch(`/grid/config/${id}`, { configData: mergedData, expectedUpdatedAt: existing.updatedAt });
      setConfigId(id);
      setFullConfigData(mergedData);
    } else {
      const createRes = await coreService.post('/grid/config', {
        name: 'Grid config',
        portfolioId: selectedPortfolioId,
        configData: newData
      });
      setConfigId(createRes.data.id);
      setFullConfigData(newData);
    }
  }, [selectedPortfolioId]);

  // Save stock config (create or update for one symbol)
  const handleSubmit = async (values: GridTradingFormValues) => {
    if (!editingSymbol) return;
    // Synchronous guard: two quick submits used to open two confirms and send two PATCHes.
    if (saveInFlightRef.current) return;
    saveInFlightRef.current = true;
    // Set when the update confirm opens: it owns the release from then on, so the button
    // stays busy while the dialog is up instead of re-enabling behind it.
    let releasedByConfirm = false;
    const releaseSave = () => {
      saveInFlightRef.current = false;
      setLoading(false);
    };
    try {
      setLoading(true);

      // Multi-symbol per portfolio is supported: persistConfigData merges into the
      // existing DataConfig (one config per portfolio, many InstrumentConfig rows). No
      // need to gate a new symbol behind closing the existing one.
      const hasLevels = priceLevels.some(l => l.quantity > 0);
      if (!hasLevels && volumeRanges.length === 0) {
        modal.warning({
          title: 'Missing required input',
          content: 'Please configure price levels with quantities before saving.',
        });
        setLoading(false);
        return;
      }

      // Validate quantity is multiplier of 100
      const invalidLevel = priceLevels.find(l => l.quantity > 0 && l.quantity % 100 !== 0);
      if (invalidLevel) {
        modal.warning({
          title: 'Invalid quantity',
          content: `Quantity ${invalidLevel.quantity} at price ${invalidLevel.price} must be a multiplier of 100.`,
        });
        setLoading(false);
        return;
      }

      for (const range of volumeRanges) {
        if (range.minPrice >= range.maxPrice) {
          modal.warning({
            title: 'Invalid price range',
            content: `Min price (${range.minPrice}) must be less than max price (${range.maxPrice}). Please edit the range in "Volume by Price Range".`,
          });
          setLoading(false);
          return;
        }
        if (range.volume <= 0) {
          modal.warning({
            title: 'Invalid volume',
            content: `Volume for the range ${range.minPrice}–${range.maxPrice} must be greater than 0. Please edit the range in "Volume by Price Range".`,
          });
          setLoading(false);
          return;
        }
      }

      // NAV cap: sum of all symbols' capacity must not exceed currentNav
      if (currentNav !== null) {
        const otherCapital = Object.entries(fullConfigData)
          .filter(([sym]) => sym !== editingSymbol)
          .reduce((sum, [, cfg]) => sum + (Number(cfg.capacity) || 0), 0);
        const totalCapitalVnd = otherCapital + Number(values.capacity);
        if (totalCapitalVnd > currentNav) {
          modal.warning({
            title: 'Capital Limit Exceeded',
            content: `Total capital across all instruments would be ${formatNumber(totalCapitalVnd)} VND, which exceeds the purchasing power of ${formatNumber(currentNav)} VND. Please reduce the capital.`,
          });
          setLoading(false);
          return;
        }
      }

      const stockConfig = {
        allow_to_exceed_floor_ceiling: values.allow_to_exceed_floor_ceiling,
        grid_interval: values.grid_interval,
        // take_profit_point intentionally omitted: the grid closes each level at the next
        // level up, so the BE derives it as = grid_interval. Sending it here would pin a
        // value nobody can see or edit — there is no input for it in this form.
        volume_by_price_range: priceLevels.length > 0 ? convertPriceLevelsToRanges(priceLevels) : convertFromTableData(volumeRanges),
        grid_type: values.grid_type,
        grid_origin: Number(values.grid_origin),
        grid_height: values.grid_height,
        capacity: values.capacity,
        stop_loss_quantity: values.stop_loss_quantity,
        stop_operating_threshold: values.stop_operating_threshold == null ? Infinity : Number(values.stop_operating_threshold),
        forced_close: values.forced_close
      };

      const newData = { ...fullConfigData, [editingSymbol]: stockConfig };
      const isUpdate = editingSymbol in fullConfigData;

      const doSave = async () => {
        await persistConfigData(newData);
        setRangesManuallyEdited(true);
        setUnsavedSymbols(prev => { const next = new Set(prev); next.delete(editingSymbol); return next; });
        setFormDirty(false);
        message.success(`${editingSymbol} configuration ${isUpdate ? 'updated' : 'created'} successfully.`);
        // Shadow-write created/updated the InstrumentConfig row server-side; tell the
        // capital panel to re-fetch so its deposit/withdraw controls become active.
        void capitalPanelRef.current?.refresh();
      };

      if (isUpdate) {
        const intendedBuy = priceLevels.reduce((sum, l) => {
          const matched = positionMatchedQty[l.price];
          const matchedQty = ((matched?.long ?? 0) + (matched?.short ?? 0));
          const unmatchedQty = Math.max(0, l.quantity - matchedQty);
          return sum + l.price * 1000 * unmatchedQty;
        }, 0);
        const allocationExceeds = priceLevels.length > 0 && purchasingPower != null && (purchasingPower - intendedBuy) < 0;
        const cashWarning = allocationExceeds ? ' This will use all your available cash.' : '';

        let confirmContent: string;
        if (isBotRunning) {
          if (values.stop_loss_quantity > 0) {
            const priceLevelSet = new Set(priceLevels.map(l => l.price));
            const outsideCount = Object.entries(positionMatchedQty)
              .filter(([priceStr]) => !priceLevelSet.has(Number(priceStr)))
              .filter(([, m]) => ((m.long ?? 0) + (m.short ?? 0)) > 0).length;
            confirmContent = `The bot is currently running. Grid will shift ${values.stop_loss_quantity} level(s) for ${outsideCount} position(s) outside the grid. These changes will be applied immediately to the running bot.${cashWarning}`;
          } else {
            confirmContent = `The bot is currently running. These changes will be applied immediately to the running bot for ${editingSymbol}. Are you sure you want to update?${cashWarning}`;
          }
        } else if (values.stop_loss_quantity > 0) {
          const priceLevelSet = new Set(priceLevels.map(l => l.price));
          const outsideCount = Object.entries(positionMatchedQty)
            .filter(([priceStr]) => !priceLevelSet.has(Number(priceStr)))
            .filter(([, m]) => ((m.long ?? 0) + (m.short ?? 0)) > 0).length;
          confirmContent = `The bot is stopped. When the grid next starts, it will shift ${values.stop_loss_quantity} level(s) for ${outsideCount} position(s) outside the grid.${cashWarning}`;
        } else {
          confirmContent = `The bot is stopped. These changes take effect when the grid for ${editingSymbol} next starts. Are you sure you want to update?${cashWarning}`;
        }
        modal.confirm({
          title: 'Update Configuration',
          content: confirmContent,
          okText: 'Update',
          cancelText: 'Cancel',
          okButtonProps: { style: { background: '#F0B90B', borderColor: '#F0B90B', color: '#1E2329' } },
          onOk: async () => {
            try {
              await doSave();
            } catch (error: any) {
              console.error('Error saving configuration:', error);
              modal.error({
                title: 'Save Failed',
                content: error.response?.data?.message || 'Failed to save configuration. Please try again.',
              });
            } finally {
              releaseSave();
            }
          },
          onCancel: releaseSave,
        });
        releasedByConfirm = true;
        return;
      }

      await doSave();
    } catch (error: any) {
      console.error('Error saving configuration:', error);
      modal.error({
        title: 'Save Failed',
        content: error.response?.data?.message || 'Failed to save configuration. Please try again.',
      });
    } finally {
      if (!releasedByConfirm) releaseSave();
    }
  };

  // `silent`: a background poll — no spinner, and a transient error keeps what is shown.
  const fetchPositions = useCallback(async (silent = false) => {
    if (!selectedPortfolioId) return;
    try {
      if (!silent) setLoadingPositions(true);
      const response = await coreService.get(`/grid/portfolio/${selectedPortfolioId}/position`);
      const raw = response.data?.data ?? response.data;
      setPositionData(raw && typeof raw === 'object' ? raw : null);
    } catch (err: any) {
      const notFound = err.response?.status === 404;
      if (!notFound) console.error('Failed to fetch positions:', err);
      if (notFound || !silent) setPositionData(null);
    } finally {
      if (!silent) setLoadingPositions(false);
    }
  }, [selectedPortfolioId]);

  // Fills land while the bot runs; positions were only read on clicks before.
  useEffect(() => {
    if (!isBotRunning || !selectedPortfolioId) return;
    const id = window.setInterval(() => { void fetchPositions(true); }, LIVE_POLL_MS);
    return () => window.clearInterval(id);
  }, [isBotRunning, selectedPortfolioId, fetchPositions]);

  // A phase change (started, stopped, crashed) moves money and positions: reload what shows
  // them, including the capital panel and history whose Cancel depends on the bot being down.
  const lastPhaseRef = useRef(botPhase);
  useEffect(() => {
    const prev = lastPhaseRef.current;
    lastPhaseRef.current = botPhase;
    if (prev === botPhase || prev === 'unknown' || botPhase === 'unknown') return;
    setTabRefreshKey((k) => k + 1);
    void fetchPositions();
  }, [botPhase, fetchPositions]);

  // Delete config for the current symbol
  const [deletingConfig, setDeletingConfig] = useState(false);
  const handleDeleteConfig = useCallback(async () => {
    if (!configId || !editingSymbol) return;
    // Defensive guard — the button is already disabled while running, but block
    // here too in case this is reached programmatically. BE also rejects (409).
    if (isBotRunning) {
      modal.error({
        title: 'Action Not Allowed',
        content: 'Cannot remove a symbol while the bot is running. Stop the bot first.',
      });
      return;
    }
    try {
      setDeletingConfig(true);
      await coreService.delete(`/grid/config/${configId}/symbol/${editingSymbol}`);
      // Remove from local state
      const updated = { ...fullConfigData };
      delete updated[editingSymbol];
      setFullConfigData(updated);
      // Reset editing symbol to trigger auto-selection
      setUnsavedSymbols(prev => { const next = new Set(prev); next.delete(editingSymbol); return next; });
      setEditingSymbol(null);
      // Reset form to defaults
      form.resetFields();
      setVolumeRanges([]);
      setPriceLevels([]);
      setRangesManuallyEdited(false);
      // Reload positions to reflect changes
      fetchPositions();
      message.success(`${editingSymbol} configuration deleted`);
    } catch (err: any) {
      modal.error({
        title: 'Delete Failed',
        content: err.response?.data?.message || 'Failed to delete configuration.',
      });
    } finally {
      setDeletingConfig(false);
    }
  }, [configId, editingSymbol, fullConfigData, fetchPositions]); // eslint-disable-line react-hooks/exhaustive-deps

  // End from broker READY: the server already removed the symbol, so reload rather than edit locally.
  const handleSymbolEnded = useCallback((symbol: string | null) => {
    if (symbol) {
      setUnsavedSymbols(prev => { const next = new Set(prev); next.delete(symbol); return next; });
      if (editingSymbol === symbol) {
        setEditingSymbol(null);
        form.resetFields();
        setVolumeRanges([]);
        setPriceLevels([]);
        setRangesManuallyEdited(false);
      }
      message.success(`${symbol} ended from broker`);
    }
    void fetchConfig(selectedPortfolioId);
    void fetchPositions();
    setTabRefreshKey((k) => k + 1);
  }, [editingSymbol, selectedPortfolioId, fetchConfig, fetchPositions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stop is blocked during ATC: 14:29–15:30 Vietnam time (UTC+7).
  const refuseStopDuringAtc = useCallback((): boolean => {
    const nowVN = new Date(Date.now() + 7 * 60 * 60 * 1000);
    const totalMinutesVN = nowVN.getUTCHours() * 60 + nowVN.getUTCMinutes();
    if (totalMinutesVN < 14 * 60 + 29 || totalMinutesVN > 15 * 60 + 30) return false;
    modal.error({
      title: 'Action Not Allowed',
      content: 'Stopping the grid bot is not allowed during the ATC session (14:29–15:30 Vietnam time). Please try again after the session ends.',
      okText: 'OK',
    });
    return true;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showStopError = (err: any) => {
    const raw = err.response?.data?.message;
    const msg = Array.isArray(raw) ? raw.join(', ') : raw;
    modal.error({
      title: 'Stop Failed',
      content: msg || 'Failed to stop grid bot. Please try again.',
    });
  };

  const refreshPausedSymbols = useCallback(async () => {
    if (!configId || !isBotUp(botPhase)) {
      setPausedSymbols(new Set());
      return;
    }
    try {
      const res = await coreService.get<{ paused: string[] }>(`/grid/config/${configId}/paused`);
      setPausedSymbols(new Set(res.data?.paused ?? []));
    } catch {
      // Unknown is shown as not paused; Stop/Start still go through the server's own checks.
      setPausedSymbols(new Set());
    }
  }, [configId, botPhase]);

  useEffect(() => {
    void refreshPausedSymbols();
  }, [refreshPausedSymbols]);

  // Stop one symbol: the bot keeps running the others, and stops itself after the last one.
  const [stoppingGrid, setStoppingGrid] = useState(false);
  const handleStopGrid = useCallback(async () => {
    if (!editingSymbol || !selectedPortfolioId) return;
    if (refuseStopDuringAtc()) return;
    if (stopInFlightRef.current) return;
    stopInFlightRef.current = true;
    try {
      setStoppingGrid(true);
      await coreService.post('/grid/config/stop-bot', {
        portfolioId: selectedPortfolioId,
        symbol: editingSymbol,
      });
      void refreshBotPhase();
      message.success(`Stopping ${editingSymbol} — the other symbols keep running`);
      void refreshPausedSymbols();
      fetchPositions();
      setTabRefreshKey(k => k + 1);
    } catch (err: any) {
      showStopError(err);
    } finally {
      stopInFlightRef.current = false;
      setStoppingGrid(false);
    }
  }, [editingSymbol, selectedPortfolioId, fetchPositions, refreshBotPhase, refuseStopDuringAtc, refreshPausedSymbols]); // eslint-disable-line react-hooks/exhaustive-deps

  // Start one paused symbol again while the rest of the bot keeps running.
  const [resumingGrid, setResumingGrid] = useState(false);
  const handleResumeGrid = useCallback(async () => {
    if (!editingSymbol || !selectedPortfolioId) return;
    if (stopInFlightRef.current) return;
    stopInFlightRef.current = true;
    try {
      setResumingGrid(true);
      await coreService.post('/grid/config/resume-bot', {
        portfolioId: selectedPortfolioId,
        symbol: editingSymbol,
      });
      message.success(`Starting ${editingSymbol} again — it resumes within ~10 seconds`);
      void refreshPausedSymbols();
      void refreshBotPhase();
    } catch (err: any) {
      const raw = err.response?.data?.message;
      const msg = Array.isArray(raw) ? raw.join(', ') : raw;
      modal.error({ title: 'Start Failed', content: msg || 'Failed to start this symbol. Please try again.' });
    } finally {
      stopInFlightRef.current = false;
      setResumingGrid(false);
    }
  }, [editingSymbol, selectedPortfolioId, refreshPausedSymbols, refreshBotPhase]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stop the whole bot. The execution stays STOPPING until the worker reports it finished.
  const [stoppingAll, setStoppingAll] = useState(false);
  const handleStopAll = useCallback(async () => {
    if (!configId) return;
    if (refuseStopDuringAtc()) return;
    if (stopInFlightRef.current) return;
    stopInFlightRef.current = true;
    try {
      setStoppingAll(true);
      await coreService.post(`/grid/config/${configId}/stop`);
      void refreshBotPhase();
      message.success('Stopping the grid bot…');
      fetchPositions();
      setTabRefreshKey(k => k + 1);
    } catch (err: any) {
      showStopError(err);
    } finally {
      stopInFlightRef.current = false;
      setStoppingAll(false);
    }
  }, [configId, fetchPositions, refreshBotPhase, refuseStopDuringAtc]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close position handler — sets forced_close to true in config
  const [closingPosition, setClosingPosition] = useState(false);
  const handleClosePosition = useCallback(async () => {
    if (!editingSymbol || !configId) return;
    try {
      setClosingPosition(true);
      const existingConfig = fullConfigData[editingSymbol];
      if (!existingConfig) return;
      const updatedConfig = { ...existingConfig, forced_close: true };
      const newData = { ...fullConfigData, [editingSymbol]: updatedConfig };
      await persistConfigData(newData);
      form.setFieldValue('forced_close', true);
      message.success(
        isBotRunning
          ? `Close position enabled for ${editingSymbol}. The bot is running, so it starts selling on the next tick.`
          : `Close position enabled for ${editingSymbol}. It takes effect when the grid next starts.`,
      );
    } catch (err: any) {
      const raw = err.response?.data?.message;
      const msg = Array.isArray(raw) ? raw.join(', ') : raw;
      modal.error({
        title: 'Close Position Failed',
        content: msg || 'Failed to update configuration. Please try again.',
      });
    } finally {
      setClosingPosition(false);
    }
  }, [editingSymbol, configId, fullConfigData, persistConfigData, form, isBotRunning]);

  const handleDisableClosePosition = useCallback(async () => {
    if (!editingSymbol || !configId) return;
    try {
      setClosingPosition(true);
      const existingConfig = fullConfigData[editingSymbol];
      if (!existingConfig) return;
      const updatedConfig = { ...existingConfig, forced_close: false };
      const newData = { ...fullConfigData, [editingSymbol]: updatedConfig };
      await persistConfigData(newData);
      form.setFieldValue('forced_close', false);
      message.success(`Close position disabled for ${editingSymbol}.`);
    } catch (err: any) {
      const raw = err.response?.data?.message;
      const msg = Array.isArray(raw) ? raw.join(', ') : raw;
      modal.error({
        title: 'Disable Close Position Failed',
        content: msg || 'Failed to update configuration. Please try again.',
      });
    } finally {
      setClosingPosition(false);
    }
  }, [editingSymbol, configId, fullConfigData, persistConfigData, form]);

  // Place grid handler
  const [placingGrid, setPlacingGrid] = useState(false);
  const [placeConfirmVisible, setPlaceConfirmVisible] = useState(false);
  const [allocationPlan, setAllocationPlan] = useState<AllocationPlan | null>(null);
  const [allocationOpen, setAllocationOpen] = useState(false);
  // Place refused by an open import on this grid: its progress, in a modal
  const [placeImportConflict, setPlaceImportConflict] = useState<ImportInProgress | null>(null);

  // Compute estimated cost per symbol from fullConfigData, split into hold value and intended buy
  const placeGridSummary = useMemo(() => {
    // Find position entry by price with tolerance (keys may differ in decimal formatting)
    const findByPrice = (map: Record<string, any>, price: number): any => {
      // Try exact string match first
      if (map[String(price)] != null) return map[String(price)];
      // Fall back to numeric comparison with tolerance
      for (const [key, val] of Object.entries(map)) {
        if (Math.abs(Number(key) - price) < 0.001) return val;
      }
      return null;
    };

    return Object.entries(fullConfigData).map(([symbol, cfg]) => {
      const ranges: number[][] = Array.isArray(cfg.volume_by_price_range) ? cfg.volume_by_price_range : [];
      const capacity = Number(cfg.capacity) || 0;

      // Get position data for this symbol
      const symPos = findSymbolPosition(positionData || {}, symbol);
      const longMap = symPos?.long && typeof symPos.long === 'object' ? symPos.long : {};
      const shortMap = symPos?.short && typeof symPos.short === 'object' ? symPos.short : {};

      let intendedBuy = 0;
      let totalQty = 0;

      for (const r of ranges) {
        const price = r[0] || 0;
        const qty = r[2] || 0;
        totalQty += qty;

        const longData = findByPrice(longMap, price);
        const shortData = findByPrice(shortMap, price);
        const matchedQty = Number(longData?.opening_amount ?? 0) + Number(shortData?.closing_amount ?? 0);

        intendedBuy += price * 1000 * Math.max(0, qty - matchedQty);
      }

      // Amount paid = total value of ALL open positions (not limited to grid levels)
      const holdValue =
        Object.entries(longMap).reduce((sum, [priceStr, data]) => sum + Number(priceStr) * 1000 * Number((data as any)?.opening_amount ?? 0), 0) +
        Object.entries(shortMap).reduce((sum, [priceStr, data]) => sum + Number(priceStr) * 1000 * Number((data as any)?.closing_amount ?? 0), 0);

      return { symbol, totalQty, holdValue, intendedBuy, capacity };
    });
  }, [fullConfigData, positionData, findSymbolPosition]);

  // Fetch existing schedule for current portfolio
  const fetchSchedule = useCallback(async () => {
    if (!selectedPortfolioId) return;
    try {
      setScheduleLoading(true);
      const res = await coreService.get('/daily');
      const raw = res.data?.data ?? res.data;
      const list: DailySchedule[] = Array.isArray(raw) ? raw : raw ? [raw] : [];
      // /daily returns every schedule the user owns (pair ones included). Only
      // this portfolio's GRID schedule belongs in this strip.
      const match = list.find(
        (sch) => sch.portfolioId === selectedPortfolioId && (!strategyId || sch.strategyId === strategyId),
      );
      setSchedule(match ?? null);
    } catch {
      setSchedule(null);
    } finally {
      setScheduleLoading(false);
    }
  }, [selectedPortfolioId, strategyId]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  const handlePlaceGrid = useCallback(async () => {
    if (isTerminalAccount(selectedPortfolio)) {
      const notice = modal.info({
        title: 'Started from the terminal',
        content: (
          <>
            {TERMINAL_START_MESSAGE}{' '}
            <DesktopAppLink onOpen={() => { notice.destroy(); navigate(DESKTOP_APP_PATH); }} />
          </>
        ),
      });
      return;
    }
    if (!configId) {
      modal.warning({ title: 'No Configuration', content: 'Save a configuration first before placing grid.' });
      return;
    }
    // No target-buy-vs-PP gate: the grid deploys each symbol's own allocated capital,
    // and total deposits are already capped at broker PP via `assertDepositWithinCap`
    // server-side. Any residual mismatch is handled by the broker rejecting individual
    // orders that can't be afforded — the bot recovers without UI intervention.
    // Busy from the first click: the schedule and plan reads below used to let every
    // further click fire them again.
    if (placeInFlightRef.current) return;
    placeInFlightRef.current = true;
    setPlacingGrid(true);
    try {
      await fetchSchedule(); // Wait for schedule to be fetched
      setShowScheduleForm(false);

      // A grid whose wallets are all empty starts, reports healthy and never places an order.
      // Ask for the allocation first rather than letting that happen silently. The server
      // refuses the place too, so this is the friendly half of the same rule.
      try {
        const planRes = await coreService.get<AllocationPlan>(
          `/grid/config/${configId}/allocation-plan`,
        );
        if (planRes.data?.needsAllocation) {
          setAllocationPlan(planRes.data);
          setAllocationOpen(true);
          return;
        }
      } catch {
        // Could not read the plan: fall through to the ordinary confirm. The server still
        // refuses an unfunded grid, so nothing starts without capital either way.
      }
      setPlaceConfirmVisible(true);
    } finally {
      placeInFlightRef.current = false;
      setPlacingGrid(false);
    }
  }, [configId, fetchSchedule, selectedPortfolio]); // eslint-disable-line react-hooks/exhaustive-deps

  const executePlaceGrid = useCallback(async (allocations?: Record<string, number>) => {
    if (placeInFlightRef.current) return;
    placeInFlightRef.current = true;
    setPlaceConfirmVisible(false);
    try {
      setPlacingGrid(true);
      const response = await coreService.post(
        `/grid/config/${configId}/place`,
        allocations ? { allocations } : undefined,
      );
      setAllocationOpen(false);
      void refreshBotPhase();
      message.success('Grid orders placed successfully.');
      fetchPositions();
      // Switch to logs tab and flash highlight
      setBottomTabKey('logs');
      setLogsHighlight(true);
      setTimeout(() => setLogsHighlight(false), 2000);
      // Capture executionId from response so LogTab can subscribe to logs
      const execId = response.data?.executionId || response.data?.id;
      if (execId) {
        setGridExecutionId(execId);
      } else if (strategyId && selectedPortfolio?.portfolioId) {
        // Fallback: fetch latest execution for this strategy
        try {
          const token = localStorage.getItem('access_token');
          const execRes = await coreService.get<{ id: string }[]>('bot/executions', {
            headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
            params: { portfolioId: selectedPortfolio.portfolioId, strategyId },
          });
          if (Array.isArray(execRes.data) && execRes.data.length > 0) {
            setGridExecutionId(execRes.data[0].id);
          }
        } catch {
          // Non-critical, logs just won't auto-load
        }
      }
      // Bump refreshKey after executionId is set so LogViewer re-subscribes with the new executionId
      setTabRefreshKey(k => k + 1);
    } catch (err: any) {
      const open409 = importInProgressOf(err);
      if (open409) {
        setPlaceImportConflict(open409);
        return;
      }
      message.error(err.response?.data?.message || 'Failed to place grid orders.');
    } finally {
      placeInFlightRef.current = false;
      setPlacingGrid(false);
    }
  }, [configId, strategyId, selectedPortfolio?.portfolioId, fetchPositions, refreshBotPhase]);

  // Schedule state
  interface DailySchedule {
    id: string;
    cronDaily: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    strategyId?: string;
    portfolioId?: string;
  }
  const [schedule, setSchedule] = useState<DailySchedule | null>(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduleTime, setScheduleTime] = useState<dayjs.Dayjs | null>(dayjs('09:00', 'HH:mm'));
  const [scheduleActive, setScheduleActive] = useState(true);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [strategies, setStrategies] = useState<PublishedStrategy[]>([]);
  const [selectedScheduleStrategy, setSelectedScheduleStrategy] = useState<string | null>(null);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  const handleSaveSchedule = useCallback(async () => {
    if (!selectedPortfolioId || !selectedScheduleStrategy || !scheduleTime) return;
    const hours = scheduleTime.hour();
    const minutes = scheduleTime.minute();
    const cronDaily = `${minutes} ${hours} * * *`;
    try {
      setSavingSchedule(true);
      if (schedule) {
        await coreService.patch(`/daily/${schedule.id}`, { cronDaily, isActive: scheduleActive, expectedUpdatedAt: schedule.updatedAt });
      } else {
        await coreService.post('/daily', {
          portfolioId: selectedPortfolioId,
          strategyId: selectedScheduleStrategy,
          cronDaily,
          isActive: scheduleActive,
        });
      }
      await fetchSchedule();
      setShowScheduleForm(false);
      message.success(schedule ? 'Schedule updated' : 'Schedule created');
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to save schedule');
    } finally {
      setSavingSchedule(false);
    }
  }, [selectedPortfolioId, strategyId, scheduleTime, scheduleActive, schedule, fetchSchedule]);

  const handleDeleteSchedule = useCallback(async () => {
    if (!schedule) return;
    try {
      setSavingSchedule(true);
      await coreService.delete(`/daily/${schedule.id}`);
      setSchedule(null);
      setShowScheduleForm(false);
      message.success('Schedule removed');
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to delete schedule');
    } finally {
      setSavingSchedule(false);
    }
  }, [schedule]);

  // Parse cron to readable time
  const parseCronToTime = (cron: string): string => {
    const parts = cron.split(' ');
    if (parts.length >= 2) {
      const h = parts[1].padStart(2, '0');
      const m = parts[0].padStart(2, '0');
      return `${h}:${m}`;
    }
    return cron;
  };

  interface PositionRow {
    price: number;
    side: 'Long' | 'Short' | string;
    openPrice: number;
    openQty: number;
    closedQty: number;
    matchedPrice: number;
    pnl: number;
  }

  // All positions grouped by symbol
  const allSymbolPositions = useMemo(() => {
    if (!positionData) return [];
    const result: { symbol: string; capacity: number | null; rows: PositionRow[] }[] = [];
    for (const [symbol, pos] of Object.entries(positionData)) {
      if (!pos || typeof pos !== 'object') continue;
      const symbolCapacity = (pos as any).capacity != null ? Number((pos as any).capacity) : null;
      let rows: PositionRow[] = [];
      if (!Array.isArray(pos) && ('long' in pos || 'short' in pos)) {
        const longMap = (pos as any).long && typeof (pos as any).long === 'object' ? (pos as any).long : {};
        const shortMap = (pos as any).short && typeof (pos as any).short === 'object' ? (pos as any).short : {};
        const mapToRow = (priceStr: string, data: any, side: 'Long' | 'Short'): PositionRow => {
          const d = typeof data === 'object' && data !== null ? data : {};
          const openPrice = Number(d.open_price ?? priceStr);
          const matchedPrice = Number(d.matched_price ?? 0);
          const openQty = Number(d.opening_amount ?? 0);
          const closedQty = Number(d.closing_amount ?? 0);
          // Use realized_pnl/pnl from data if available, otherwise compute from prices
          let rawPnl: number;
          if (d.realized_pnl != null || d.pnl != null) {
            rawPnl = Number(d.realized_pnl ?? d.pnl ?? 0);
          } else {
            const qty = closedQty > 0 ? closedQty : openQty;
            rawPnl = matchedPrice > 0
              ? (side === 'Long' ? (matchedPrice - openPrice) * qty : (openPrice - matchedPrice) * qty)
              : 0;
          }
          return {
            price: Number(priceStr),
            side,
            openPrice,
            openQty,
            closedQty,
            matchedPrice,
            pnl: rawPnl * 1000,
          };
        };
        const longRows = Object.entries(longMap).map(([p, d]) => mapToRow(p, d, 'Long'));
        const shortRows = Object.entries(shortMap).map(([p, d]) => mapToRow(p, d, 'Short'));
        rows = [...longRows, ...shortRows].sort((a, b) => b.price - a.price);
      } else {
        const rawRows: any[] = (pos as any)?.gridPoints ?? (pos as any)?.levels ?? (pos as any)?.orders ?? (pos as any)?.gridOrders ?? (Array.isArray(pos) ? pos : []);
        rows = rawRows.map((r: any) => {
          const side = (r.side === '1' || r.side === 'BUY' ? 'Long' : r.side === '2' || r.side === 'SELL' ? 'Short' : String(r.side ?? r.type ?? '—'));
          const openPrice = Number(r.open_price ?? r.price ?? 0);
          const matchedPrice = Number(r.matched_price ?? 0);
          const openQty = Number(r.opening_amount ?? r.orderQty ?? r.quantity ?? r.qty ?? 0);
          const closedQty = Number(r.closing_amount ?? 0);
          let rawPnl: number;
          if (r.realized_pnl != null || r.pnl != null) {
            rawPnl = Number(r.realized_pnl ?? r.pnl ?? 0);
          } else {
            const qty = closedQty > 0 ? closedQty : openQty;
            rawPnl = matchedPrice > 0
              ? (side === 'Long' ? (matchedPrice - openPrice) * qty : (openPrice - matchedPrice) * qty)
              : 0;
          }
          return {
            price: Number(r.price ?? r.gridPrice ?? 0),
            side,
            openPrice,
            openQty,
            closedQty,
            matchedPrice,
            pnl: rawPnl * 1000,
          };
        }).sort((a, b) => b.price - a.price);
      }
      if (rows.length > 0) result.push({ symbol, capacity: symbolCapacity, rows });
    }
    return result;
  }, [positionData]);

  // Fetch positions when symbol changes
  useEffect(() => {
    if (editingSymbol && selectedPortfolioId) fetchPositions();
  }, [editingSymbol, selectedPortfolioId, fetchPositions]);

  // Build a map of price → matched qty from position data for the current symbol
  // Keys are rounded to 2 decimal places to avoid floating point mismatch
  const positionMatchedQty = useMemo<Record<number, { long: number; short: number }>>(() => {
    if (!positionData || !editingSymbol) return {};
    const symPos = findSymbolPosition(positionData, editingSymbol);
    if (!symPos || typeof symPos !== 'object') return {};
    const result: Record<number, { long: number; short: number }> = {};
    const longMap = symPos.long && typeof symPos.long === 'object' ? symPos.long : {};
    const shortMap = symPos.short && typeof symPos.short === 'object' ? symPos.short : {};
    for (const [priceStr, data] of Object.entries(longMap)) {
      const p = Math.round(Number(priceStr) * 100) / 100;
      const d = data as any;
      if (!result[p]) result[p] = { long: 0, short: 0 };
      result[p].long += Number(d.opening_amount ?? d.qty ?? 0);
    }
    for (const [priceStr, data] of Object.entries(shortMap)) {
      const p = Math.round(Number(priceStr) * 100) / 100;
      const d = data as any;
      if (!result[p]) result[p] = { long: 0, short: 0 };
      result[p].short += Number(d.closing_amount ?? d.qty ?? 0);
    }
    return result;
  }, [positionData, editingSymbol, findSymbolPosition]);

  const displayPrice = latestPrice !== null ? latestPrice : (selectedInstrument?.price || 0);
  const displayChangePercent = latestChangePercent !== null ? latestChangePercent : (selectedInstrument?.changePercent24h || 0);

  // --- Render fragments shared by the desktop split and the compact mobile shell ---

  /** The pills backed by the one ConfigurationSection instance. */
  const onConfigSection = CONFIG_SECTIONS.includes(mobileSection as MobileConfigSection);
  const configSection: MobileConfigSection = onConfigSection
    ? (mobileSection as MobileConfigSection)
    : 'settings';

  const symbolBar = (
    <div data-tour="grid-symbols" style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 0', overflowX: 'auto' }}>
      {stockEntries.map(({ symbol }) => (
        <Button
          key={symbol}
          size="small"
          type={editingSymbol === symbol ? 'primary' : 'default'}
          onClick={() => navigateToStock(symbol)}
          style={{
            fontSize: '12px',
            fontWeight: 600,
            ...(editingSymbol === symbol
              ? { background: '#0ECB81', borderColor: '#0ECB81' }
              : { background: '#2B3139', borderColor: '#2B3139', color: '#E0E0E0' }),
          }}
        >
          {symbol}
          {pausedSymbols.has(symbol) && (
            <PauseCircleOutlined title="Paused" aria-label="Paused" style={{ fontSize: 11, marginLeft: 2, color: editingSymbol === symbol ? '#0B0E11' : '#F0B90B' }} />
          )}
        </Button>
      ))}
      {Array.from(unsavedSymbols).filter(s => !(s in fullConfigData)).map(symbol => (
        <div key={symbol} style={{ display: 'inline-flex', alignItems: 'center', gap: '0' }}>
          <Button
            size="small"
            type={editingSymbol === symbol ? 'primary' : 'default'}
            onClick={() => navigateToStock(symbol)}
            style={{
              fontSize: '12px',
              fontWeight: 600,
              borderStyle: 'dashed',
              borderRight: 'none',
              borderRadius: '4px 0 0 4px',
              ...(editingSymbol === symbol
                ? { background: '#0ECB81', borderColor: '#0ECB81' }
                : { background: '#2B3139', borderColor: '#848E9C', color: '#E0E0E0' }),
            }}
          >
            {symbol} *
          </Button>
          <Button
            size="small"
            icon={<CloseOutlined style={{ fontSize: '10px' }} />}
            onClick={(e) => {
              e.stopPropagation();
              const remainingUnsaved = Array.from(unsavedSymbols).filter(s => s !== symbol && !(s in fullConfigData));
              setUnsavedSymbols(prev => { const next = new Set(prev); next.delete(symbol); return next; });
              if (editingSymbol === symbol) {
                setFormDirty(false);
                // Navigate to nearest symbol
                const savedSymbols = Object.keys(fullConfigData);
                const allSymbols = [...savedSymbols, ...remainingUnsaved];
                if (allSymbols.length > 0) {
                  doNavigateToStock(allSymbols[0]);
                } else {
                  setEditingSymbol(null);
                }
              }
            }}
            style={{
              fontSize: '10px',
              borderStyle: 'dashed',
              borderLeft: 'none',
              borderRadius: '0 4px 4px 0',
              padding: '0 4px',
              minWidth: 'unset',
              ...(editingSymbol === symbol
                ? { background: '#0ECB81', borderColor: '#0ECB81', color: '#fff' }
                : { background: '#2B3139', borderColor: '#848E9C', color: '#848E9C' }),
            }}
          />
        </div>
      ))}
      {/* "+" button — opens a dedicated search modal so adding a new instrument
          doesn't require fishing for the search field in the trading-parameter panel. */}
      <Button
        size="small"
        icon={<PlusOutlined style={{ fontSize: '11px' }} />}
        onClick={() => { setAddInstrumentQuery(''); setFutureSearchResults([]); setAddInstrumentOpen(true); }}
        title="Add instrument"
        style={{
          fontSize: '12px',
          fontWeight: 600,
          background: 'transparent',
          borderStyle: 'dashed',
          borderColor: '#848E9C',
          color: '#848E9C',
          padding: '0 8px',
          minWidth: 'unset',
        }}
      >
        Add
      </Button>
      <span style={{ color: '#2B3139', fontSize: '11px' }}>|</span>
      <span style={{
        fontSize: '11px',
        fontWeight: 500,
        color: displayChangePercent >= 0 ? '#0ECB81' : '#F6465D',
      }}>
        {formatDecimal(displayPrice, 2)} ({displayChangePercent >= 0 ? '+' : ''}{formatDecimal(displayChangePercent, 2)}%)
      </span>
    </div>
  );

  const chartNode = (
    <div data-tour="grid-chart" style={{ width: '100%', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <ChartSection
        selectedInstrument={selectedInstrument}
        priceData={futurePriceData}
        priceLines={gridPriceLines}
      />
    </div>
  );

  const bottomTabItems = [
  {
    label: 'Logs',
    key: 'logs',
    children: (
      <div style={{ height: '100%', overflow: 'hidden', paddingBottom: '8px' }}>
        <LogTab
          selectedPortfolio={selectedPortfolio}
          strategyId={strategyId}
          executionId={executionId}
          compact
          refreshKey={tabRefreshKey}
        />
      </div>
    ),
  },
  {
    label: 'Position',
    key: 'position',
    children: (
      <div style={{ height: '100%', overflow: 'auto', padding: '8px' }}>
        {loadingPositions ? (
          <div style={{ textAlign: 'center', padding: '16px', color: '#848E9C' }}>Loading...</div>
        ) : allSymbolPositions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '16px', color: '#848E9C', fontSize: '12px' }}>
            No positions
          </div>
        ) : (
          allSymbolPositions.map(({ symbol, rows }) => {
            const symbolPnl = rows.reduce((sum, r) => sum + r.pnl, 0);
            return (
            <div key={symbol} style={{ marginBottom: '8px' }}>
              <div
                style={{
                  padding: '4px 6px',
                  fontSize: '12px',
                  fontWeight: 700,
                  color: '#E0E0E0',
                  background: '#2B3139',
                  borderRadius: '3px',
                  marginBottom: '2px',
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
                onClick={() => { toggleSymbolExpansion(symbol); fetchPositions(); }}
              >
                <CaretDownOutlined
                  style={{
                    transform: expandedSymbols.has(symbol) ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                    fontSize: '12px'
                  }}
                />
                <span>{symbol}</span>
                {/* Same as the pair header: one line, never wrapped, so the symbol total
                    sits in the same column as the per-level figures below it. */}
                <span
                  title={`${symbolPnl > 0 ? '+' : ''}${formatNumber(Math.round(symbolPnl) || 0)}`}
                  style={{
                    marginLeft: 'auto',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: symbolPnl > 0 ? '#0ECB81' : symbolPnl < 0 ? '#F6465D' : '#848E9C',
                    fontFamily: "'Courier New', monospace",
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  R.PnL: {symbolPnl > 0 ? '+' : ''}{isMobile ? formatAbbrev(Math.round(symbolPnl) || 0) : formatNumber(Math.round(symbolPnl) || 0)}
                </span>
              </div>
              {expandedSymbols.has(symbol) && (isMobile ? (
                /* Six right-aligned columns whose headers alone read "Matched Price" and
                   "Closed Qty" cannot fit 390px; the table clipped R.PnL off the edge
                   rather than shrinking. Two lines per level, with the realized PnL where
                   the eye lands first. */
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  marginTop: 4,
                  marginBottom: 8,
                  // Matches the symbol header's 4px 6px above, so the R.PnL column lines up.
                  padding: '0 6px',
                }}>
                  {rows.map((row, idx) => (
                    <div
                      key={idx}
                      onClick={() => fetchPositions()}
                      style={{ borderBottom: '1px solid #1A1D23', paddingBottom: 6, cursor: 'pointer' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
                        <span style={{ fontWeight: 600, flexShrink: 0, color: row.side === 'Long' ? '#0ECB81' : row.side === 'Short' ? '#F6465D' : '#848E9C' }}>
                          {row.side}
                        </span>
                        <span style={{ color: '#848E9C' }}>
                          {formatNumber(row.openPrice)}
                          <span style={{ color: '#5E6673' }}> {'\u2192'} </span>
                          <b style={{ color: '#E0E0E0' }}>{formatNumber(row.matchedPrice)}</b>
                        </span>
                        <span
                          title={`${row.pnl > 0 ? '+' : ''}${formatNumber(Math.round(row.pnl) || 0)}`}
                          style={{ marginLeft: 'auto', flexShrink: 0, fontWeight: 600, fontFamily: "'Courier New', monospace", color: row.pnl > 0 ? '#0ECB81' : row.pnl < 0 ? '#F6465D' : '#848E9C' }}
                        >
                          {row.pnl > 0 ? '+' : ''}{formatAbbrev(Math.round(row.pnl) || 0)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 12, fontSize: 10.5, color: '#848E9C', marginTop: 2 }}>
                        <span>Open <b style={{ color: '#E0E0E0' }}>{formatNumber(row.openQty)}</b></span>
                        <span>Closed <b style={{ color: row.closedQty > 0 ? '#0ECB81' : '#E0E0E0' }}>{formatNumber(row.closedQty)}</b></span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', marginTop: '4px', marginBottom: '8px' }}>
                  <thead>
                    <tr style={{ color: '#848E9C', borderBottom: '1px solid #2B3139' }}>
                      <th style={{ textAlign: 'left', padding: '3px 6px', fontWeight: 600 }}>Side</th>
                      <th style={{ textAlign: 'right', padding: '3px 6px', fontWeight: 600 }}>Open Price</th>
                      <th style={{ textAlign: 'right', padding: '3px 6px', fontWeight: 600 }}>Matched Price</th>
                      <th style={{ textAlign: 'right', padding: '3px 6px', fontWeight: 600 }}>Open Qty</th>
                      <th style={{ textAlign: 'right', padding: '3px 6px', fontWeight: 600 }}>Closed Qty</th>
                      <th style={{ textAlign: 'right', padding: '3px 6px', fontWeight: 600 }}>R.PnL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #1A1D23', cursor: 'pointer' }} onClick={() => fetchPositions()}>
                        <td style={{ padding: '3px 6px', color: row.side === 'Long' ? '#0ECB81' : row.side === 'Short' ? '#F6465D' : '#848E9C', fontWeight: 600 }}>
                          {row.side}
                        </td>
                        <td style={{ padding: '3px 6px', textAlign: 'right', color: '#E0E0E0', fontWeight: 600 }}>
                          {formatNumber(row.openPrice)}
                        </td>
                        <td style={{ padding: '3px 6px', textAlign: 'right', color: '#848E9C' }}>
                          {formatNumber(row.matchedPrice)}
                        </td>
                        <td style={{ padding: '3px 6px', textAlign: 'right', color: '#E0E0E0' }}>
                          {formatNumber(row.openQty)}
                        </td>
                        <td style={{ padding: '3px 6px', textAlign: 'right', color: row.closedQty > 0 ? '#0ECB81' : '#848E9C' }}>
                          {formatNumber(row.closedQty)}
                        </td>
                        <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 600, fontFamily: "'Courier New', monospace", color: row.pnl > 0 ? '#0ECB81' : row.pnl < 0 ? '#F6465D' : '#848E9C' }}>
                          {row.pnl > 0 ? '+' : ''}{formatNumber(Math.round(row.pnl) || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ))}
            </div>
          )})

        )}
      </div>
    ),
  },
  {
    label: 'Bid/Ask',
    key: 'bidask',
    children: (
      <div style={{ height: '100%', overflow: 'auto' }}>
        <BidAsk
          height={0}
          priceData={futurePriceData}
        />
      </div>
    ),
  },
  {
    label: 'Capital',
    key: 'capital',
    children: (
      <div style={{ height: '100%', overflow: 'auto' }}>
        <CapitalHistoryTab
          portfolioId={selectedPortfolioId}
          symbol={editingSymbol ?? selectedInstrument?.symbol}
          refreshKey={tabRefreshKey}
        />
      </div>
    ),
  },
  {
    label: 'Performance',
    key: 'performance',
    children: (
      <div style={{ height: '100%', overflow: 'auto' }}>
        <GridPerformanceTab
          portfolioId={selectedPortfolioId}
          refreshKey={tabRefreshKey}
          active={(isMobile ? mobileSection : bottomTabKey) === 'performance'}
        />
      </div>
    ),
  },
  {
    // Shares the broker holds that no strategy claims — where an ex-rights hand-over
    // leaves them, and where an import takes them back. Not a portfolio: see BasicHoldings.
    label: 'Import Holdings',
    key: 'basic',
    children: (
      <div style={{ height: '100%', overflow: 'auto' }}>
        <BasicHoldings
          portfolioId={selectedPortfolioId}
          dataConfigId={configId}
          refreshKey={tabRefreshKey}
          active={(isMobile ? mobileSection : bottomTabKey) === 'basic'}
          // the import wrote the ladder and the wallet: a save from the old form would 409
          onImported={() => {
            void fetchConfig(selectedPortfolioId);
            void fetchPositions();
            setTabRefreshKey((k) => k + 1);
          }}
        />
      </div>
    ),
  },
  ];
  const bottomTab = (key: string) => bottomTabItems.find((t) => t.key === key)?.children;

  const configPanel = (
    <ConfigurationSection
      mobile={isMobile}
      mobileSection={configSection}
      onFinishFailed={() => setMobileSection('settings')}
      form={form}
      volumeRanges={volumeRanges}
      loading={loading}
      selectedInstrument={selectedInstrument}
      displayPrice={displayPrice}
      displayChangePercent={displayChangePercent}
      onFinish={handleSubmit}
      onAddRange={handleAddRange}
      onDeleteRange={handleDeleteRange}
      onRangeChange={handleRangeChange}
      onRangeUpdate={handleRangeUpdate}
      onSetRanges={handleSetRanges}
      isExistingConfig={editingSymbol !== null && editingSymbol in fullConfigData}
      configsLoading={configsLoading}
      onPlaceGrid={handlePlaceGrid}
      placingGrid={placingGrid}
      rangesManuallyEdited={rangesManuallyEdited}
      onDeleteConfig={handleDeleteConfig}
      deletingConfig={deletingConfig}
      isBotRunning={isBotRunning}
      botPhase={botPhase}
      onStopGrid={handleStopGrid}
      stoppingGrid={stoppingGrid}
      onStopAll={handleStopAll}
      stoppingAll={stoppingAll}
      onResumeGrid={handleResumeGrid}
      resumingGrid={resumingGrid}
      symbolPaused={!!editingSymbol && pausedSymbols.has(editingSymbol)}
      priceLevels={priceLevels}
      onPriceLevelsChange={setPriceLevels}
      positionMatchedQty={positionMatchedQty}
      onFormDirty={() => setFormDirty(true)}
      formDirty={formDirty}
      searchResults={futureSearchResults}
      searchLoading={futureSearchLoading}
      onSearch={handleStockSearch}
      onSelectStock={handleSelectStockFromSearch}
      editingSymbol={editingSymbol}
      portfolioId={selectedPortfolioId}
      dataConfigId={configId}
      onClosePosition={handleClosePosition}
      onDisableClosePosition={handleDisableClosePosition}
      closingPosition={closingPosition}
      savedForcedClose={!!fullConfigData[editingSymbol ?? '']?.forced_close}
      topSlot={
        <InstrumentCapitalPanel
          ref={capitalPanelRef}
          portfolioId={selectedPortfolioId}
          editingSymbol={editingSymbol}
          isSaved={editingSymbol !== null && editingSymbol in fullConfigData}
          onTotalCapitalChange={setPurchasingPower}
          poll={isMobile ? onConfigSection : activeTab === '1'}
          refreshKey={(isMobile ? onConfigSection : activeTab === '1') ? tabRefreshKey : undefined}
          botRunning={isBotRunning}
          dataConfigId={configId}
          onSymbolEnded={handleSymbolEnded}
        />
      }
    />
  );

  const desktopTabs = (
    <Tabs
      className="grid-trading-tabs"
      activeKey={activeTab}
      onChange={(key) => { setActiveTab(key); setTabRefreshKey(k => k + 1); }}
      size="small"
      style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '0 8px' }}
      items={[
        {
          label: 'Grid Config',
          key: '1',
          children: (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', paddingBottom: 4 }}>
          {/* Configured symbols bar */}
          {symbolBar}

          {/* Main content: chart + orderbook + config. Resizable splits
              (drag the dividers): chart-column ↔ config-column horizontally,
              chart ↕ bottom-tabs vertically; sizes persist per-browser. */}
          <ChartConfigSplit
            stacked={isMobile}
            drawerOpen={mobileConfigOpen}
            onOpenDrawer={() => setMobileConfigOpen(true)}
            onCloseDrawer={() => setMobileConfigOpen(false)}
            configTitle="Grid Config"
            initialSizes={[67, 33]}
            minSizes={[320, 300]}
            gap={2}
            storageKey="grid:layout:chart-config"
          >
            {/* Left: Chart + Order Book */}
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
              <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                <ChartTabsSplit stacked={isMobile} initialSizes={[60, 40]} minSizes={[200, 200]} gap={2} storageKey="grid:layout:chart-tabs">
                  {chartNode}
                  <div data-tour="grid-bottom-tabs" style={{
                    width: '100%', height: '100%', minHeight: 0, overflow: 'hidden', borderRadius: '4px', display: 'flex', flexDirection: 'column',
                    background: logsHighlight ? '#1a2a1e' : '#1E2329',
                    border: logsHighlight ? '1px solid rgba(14, 203, 129, 0.5)' : '1px solid #2B3139',
                    boxShadow: logsHighlight ? '0 0 12px rgba(14, 203, 129, 0.2)' : 'none',
                    transition: 'background 0.5s ease, border-color 0.5s ease, box-shadow 0.5s ease',
                  }}>
                    <Tabs
                      size="small"
                      activeKey={bottomTabKey}
                      onChange={(key) => { setBottomTabKey(key); if (key === 'position') fetchPositions(); }}
                      destroyOnHidden={false}
                      style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
                      tabBarStyle={{ margin: 0, padding: '0 8px', flexShrink: 0 }}
                      className="grid-trading-tabs"
                      items={bottomTabItems}
                    />
                  </div>
                </ChartTabsSplit>
              </div>
            </div>

            {/* Right: Config form */}
            <div style={{ width: '100%', height: '100%', minHeight: 0, overflow: 'hidden' }}>
              {configPanel}
            </div>
          </ChartConfigSplit>
        </div>
          ),
        },
        {
          label: 'Order and Transactions',
          key: '3',
          children: (
            <OrderAndTransactionsTab selectedPortfolio={selectedPortfolio} active={activeTab === '3'} refreshKey={tabRefreshKey} pollMs={isBotRunning ? LIVE_POLL_MS : undefined} />
          ),
        },
        {
          label: 'Portfolio',
          key: '4',
          children: (
            <PortfolioTab selectedPortfolio={selectedPortfolio} active={activeTab === '4'} refreshKey={tabRefreshKey} pollMs={isBotRunning ? LIVE_POLL_MS : undefined} configType="GRID" />
          ),
        },
      ]}
    />
  );

  /**
   * Compact shell: one scrolling column instead of the two-column split, and every
   * destination that was a nested tab is promoted to a pill — a config group, a table
   * and the chart are all one tap away rather than two levels deep behind a drawer.
   */
  const mobileSections: ShellSection[] = [
    {
      key: 'settings',
      label: 'Settings',
      content: configPanel,
      badge: formDirty ? <UnsavedDot /> : undefined,
    },
    { key: 'levels', label: 'Levels', content: configPanel },
    { key: 'position', label: 'Position', content: bottomTab('position'), ownScroll: true },
    { key: 'logs', label: 'Logs', content: bottomTab('logs'), ownScroll: true },
    { key: 'bidask', label: 'Bid/Ask', content: bottomTab('bidask'), ownScroll: true },
    { key: 'capital', label: 'Capital', content: bottomTab('capital'), ownScroll: true },
    {
      key: 'orders',
      label: 'Orders',
      hideChart: true,
      ownScroll: true,
      content: (
        <OrderAndTransactionsTab
          selectedPortfolio={selectedPortfolio}
          active={mobileSection === 'orders'}
          refreshKey={tabRefreshKey}
          pollMs={isBotRunning ? LIVE_POLL_MS : undefined}
        />
      ),
    },
    {
      key: 'performance',
      label: 'Performance',
      hideChart: true,
      ownScroll: true,
      content: bottomTab('performance'),
    },
    {
      // hideChart like its neighbours: it is a table to read, not something to watch a
      // price against.
      key: 'basic',
      label: 'Import',
      hideChart: true,
      ownScroll: true,
      content: bottomTab('basic'),
    },
    {
      key: 'portfolio',
      label: 'Portfolio',
      hideChart: true,
      ownScroll: true,
      content: (
        <PortfolioTab
          selectedPortfolio={selectedPortfolio}
          active={mobileSection === 'portfolio'}
          refreshKey={tabRefreshKey}
          pollMs={isBotRunning ? LIVE_POLL_MS : undefined}
          configType="GRID"
        />
      ),
    },
    { key: 'advanced', label: 'Advanced', content: configPanel },
  ];

  const mobileBody = (
    <MobileTradingShell
      storageKey="grid:mobile"
      chartHeightVh={30}
      topSlot={
        <div style={{ flexShrink: 0, padding: '4px 8px', borderBottom: '1px solid #2B3139' }}>
          {symbolBar}
        </div>
      }
      chart={chartNode}
      sections={mobileSections}
      activeKey={mobileSection}
      onChange={(key) => {
        setMobileSection(key);
        setTabRefreshKey((k) => k + 1);
        if (key === 'position') fetchPositions();
      }}
      actionBar={
        onConfigSection ? (
          <ConfigActionBar
            mobile
            form={form}
            isExistingConfig={editingSymbol !== null && editingSymbol in fullConfigData}
            editingSymbol={editingSymbol}
            loading={loading}
            formDirty={formDirty}
            savedForcedClose={!!fullConfigData[editingSymbol ?? '']?.forced_close}
            onStopGrid={handleStopGrid}
            stoppingGrid={stoppingGrid}
            onStopAll={handleStopAll}
            stoppingAll={stoppingAll}
            onResumeGrid={handleResumeGrid}
            resumingGrid={resumingGrid}
            symbolPaused={!!editingSymbol && pausedSymbols.has(editingSymbol)}
            onPlaceGrid={handlePlaceGrid}
            placingGrid={placingGrid}
            botPhase={botPhase}
          />
        ) : undefined
      }
    />
  );

  return (
    <div style={{ height: isMobile ? '100%' : '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', background: 'linear-gradient(135deg, #1A1D23 0%, #1a1d26 25%, #0f1115 50%, #1a1d26 75%, #1A1D23 100%)' }}>
        {/* Tabs + Popconfirm button styling */}
        <style>{`
          .grid-trading-tabs,
          .grid-trading-tabs .ant-tabs-content-holder,
          .grid-trading-tabs .ant-tabs-content,
          .grid-trading-tabs .ant-tabs-tabpane-active {
            flex: 1; min-height: 0; display: flex; flex-direction: column; overflow: hidden;
          }
          .grid-trading-tabs .ant-tabs-nav { flex-shrink: 0; }
          .grid-popconfirm-buttons .ant-btn { min-width: 72px; height: 32px; }
          .grid-popconfirm-buttons .ant-btn-default:hover { background: #3d4148 !important; color: #e0e0e0 !important; border-color: #3d4148 !important; }
          .grid-popconfirm-buttons .ant-btn-dangerous:hover,
          .grid-popconfirm-buttons .ant-btn-danger:hover { background: #ff7875 !important; color: #fff !important; border-color: #ff7875 !important; }
          .grid-edit-range-modal .ant-modal-footer .ant-btn { min-width: 72px; height: 32px; }
          .grid-edit-range-modal .ant-modal-footer .ant-btn-default:hover { background: #3d4148 !important; color: #e0e0e0 !important; border-color: #3d4148 !important; }
          .grid-edit-range-modal .ant-modal-footer .ant-btn-primary:hover { background: #0bb369 !important; border-color: #0bb369 !important; color: #fff !important; }
          .execute-place-grid-btn.ant-btn-primary:disabled,
          .execute-place-grid-btn.ant-btn-primary[disabled] { background: #2B3139 !important; border-color: #3d4148 !important; color: #848E9C !important; opacity: 1 !important; }
          .grid-level-input.ant-input-number { background: transparent !important; border: none !important; border-bottom: 1px solid #2B3139 !important; border-radius: 0 !important; box-shadow: none !important; }
          .grid-level-input .ant-input-number-handler-wrap { display: none !important; }
          .grid-level-input.ant-input-number:hover { border-bottom-color: #0ECB81 !important; }
          .grid-level-input.ant-input-number-focused, .grid-level-input.ant-input-number:focus { border-bottom-color: #0ECB81 !important; box-shadow: none !important; }
          .grid-level-input .ant-input-number-input { padding: 0 !important; }
          .grid-price-origin .ant-input-number-input { color: #0ECB81 !important; }
          .grid-price-limit .ant-input-number-input { color: #F6465D !important; }
          .grid-advance-switch.ant-switch { background: #3d4148 !important; }
          .grid-advance-switch.ant-switch-checked { background: #0ECB81 !important; }
          .grid-advance-switch.ant-switch:hover:not(.ant-switch-disabled) { opacity: 0.9; }
          .grid-advance-switch.ant-switch-checked:hover:not(.ant-switch-disabled) { background: #0bb369 !important; opacity: 1; }
        `}</style>
        <TradingPageHeader
          pageName="Grid Trading"
          portfolios={portfolios}
          selectedPortfolioId={selectedPortfolioId}
          onChangePortfolio={setSelectedPortfolioId}
          loadingPortfolios={loadingPortfolios}
          // Also in the header, not only beside the config fields: the chart is
          // back-adjusted too, so the whole page is showing re-based numbers and the notice
          // should survive scrolling the config panel out of view.
          rightExtra={
            <>
              {editingSymbol && <CorporateActionBadge symbols={[editingSymbol]} />}
              <OnboardingTour
                storageKey={GRID_ONBOARDING_KEY}
                steps={gridOnboardingSteps({
                demoSearch: {
                  start: () => {
                    setAddInstrumentDemo(true);
                    setAddInstrumentQuery(ONBOARDING_DEMO_SYMBOL);
                    setAddInstrumentOpen(true);
                    handleStockSearch(ONBOARDING_DEMO_SYMBOL);
                  },
                  stop: () => {
                    setAddInstrumentDemo(false);
                    setAddInstrumentOpen(false);
                    setAddInstrumentQuery('');
                    setFutureSearchResults([]);
                  },
                },
              })}
              />
            </>
          }
        />
        {isMobile ? mobileBody : desktopTabs}

      <ImportInProgressModal
        conflict={placeImportConflict}
        onClose={() => setPlaceImportConflict(null)}
        onReady={() => fetchPositions()}
      />

      {/* Add Instrument — dedicated search modal */}
      <Modal
        title="Add instrument"
        rootClassName="grid-add-instrument-modal"
        open={addInstrumentOpen}
        onCancel={() => setAddInstrumentOpen(false)}
        footer={null}
        width={420}
        destroyOnHidden
      >
        <Input
          autoFocus
          allowClear
          prefix={<SearchOutlined style={{ color: '#848E9C' }} />}
          placeholder="Search symbol (e.g. VNM, HPG, VN30F2511)"
          value={addInstrumentQuery}
          readOnly={addInstrumentDemo}
          onChange={(e) => {
            const v = e.target.value;
            setAddInstrumentQuery(v);
            handleStockSearch(v);
          }}
          onPressEnter={() => {
            if (addInstrumentDemo) return;
            // Quick path: typed a clean symbol → jump straight in without picking.
            const q = addInstrumentQuery.trim().toUpperCase();
            if (!q) return;
            const matched = futureSearchResults.find((r) => r.symbol.toUpperCase() === q);
            const symbol = matched ? matched.symbol : q;
            setAddInstrumentOpen(false);
            handleSelectStockFromSearch(symbol);
          }}
          style={{ marginBottom: 12 }}
        />
        <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #2B3139', borderRadius: 4 }}>
          {futureSearchLoading ? (
            <div style={{ textAlign: 'center', padding: 16 }}>
              <Spin size="small" />
            </div>
          ) : futureSearchResults.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 16, color: '#848E9C', fontSize: 12 }}>
              {addInstrumentQuery.trim() ? 'No matches' : 'Type to search'}
            </div>
          ) : (
            futureSearchResults.map((r) => {
              const alreadyAdded = r.symbol in fullConfigData;
              return (
                <div
                  key={r.symbol}
                  onClick={() => {
                    if (addInstrumentDemo) return;
                    setAddInstrumentOpen(false);
                    handleSelectStockFromSearch(r.symbol);
                  }}
                  style={{
                    padding: '8px 12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    borderBottom: '1px solid #1E2329',
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = '#1E2329'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                >
                  <span style={{ color: '#E0E0E0', fontWeight: 600 }}>
                    {r.symbol}
                    {alreadyAdded && (
                      <span style={{ marginLeft: 8, color: '#848E9C', fontWeight: 400, fontSize: 11 }}>
                        already added
                      </span>
                    )}
                  </span>
                  <span
                    style={{
                      color: r.changePercent24h >= 0 ? '#0ECB81' : '#F6465D',
                      fontVariantNumeric: 'tabular-nums',
                      fontSize: 12,
                    }}
                  >
                    {r.price ? formatNumber(r.price) : '—'}
                    {r.changePercent24h != null && Number.isFinite(r.changePercent24h) ? (
                      <span style={{ marginLeft: 6 }}>
                        ({r.changePercent24h >= 0 ? '+' : ''}{formatDecimal(r.changePercent24h, 2)}%)
                      </span>
                    ) : null}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </Modal>

      {/* Place Grid Confirmation Modal */}
      <Modal
        title="Place Grid — Order Summary"
        open={placeConfirmVisible}
        onCancel={() => setPlaceConfirmVisible(false)}
        footer={null}
        width={520}
        destroyOnHidden
        styles={{ body: { padding: '16px 0' } }}
      >
        {selectedPortfolio && (
          <AccountSnapshot accounts={[{ id: selectedPortfolio.id, label: selectedPortfolio.portfolioName }]} />
        )}
        {/* Summary table */}
        <div style={{ marginBottom: 20 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2B3139', color: '#848E9C', fontSize: 12 }}>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Symbol</th>
                <th style={{ textAlign: 'right', padding: '8px 12px' }}>Qty</th>
                <th style={{ textAlign: 'right', padding: '8px 12px' }}>Amount Paid</th>
                <th style={{ textAlign: 'right', padding: '8px 12px' }}>Target Buy</th>
                <th style={{ textAlign: 'right', padding: '8px 12px' }}>Capital</th>
              </tr>
            </thead>
            <tbody>
              {placeGridSummary.map(({ symbol, totalQty, holdValue, intendedBuy, capacity }) => (
                <tr key={symbol} style={{ borderBottom: '1px solid #2B3139' }}>
                  <td style={{ padding: '10px 12px', fontWeight: 600, color: '#E0E0E0' }}>{symbol}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#E0E0E0' }}>{formatNumber(totalQty)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#0ECB81', fontWeight: 500 }}>
                    {formatInt(holdValue)}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: intendedBuy > capacity ? '#F6465D' : '#faad14', fontWeight: 600 }}>
                    {formatInt(intendedBuy)}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#E0E0E0' }}>
                    {formatNumber(capacity)}
                  </td>
                </tr>
              ))}
              {placeGridSummary.length > 1 && (
                <tr style={{ borderTop: '2px solid #2B3139', fontWeight: 700 }}>
                  <td style={{ padding: '10px 12px', color: '#E0E0E0' }}>Total</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#E0E0E0' }}>
                    {formatNumber(placeGridSummary.reduce((s, r) => s + r.totalQty, 0))}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#0ECB81', fontWeight: 700 }}>
                    {formatInt(placeGridSummary.reduce((s, r) => s + r.holdValue, 0))}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#faad14', fontWeight: 700 }}>
                    {formatInt(placeGridSummary.reduce((s, r) => s + r.intendedBuy, 0))}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#E0E0E0' }}>
                    {formatNumber(placeGridSummary.reduce((s, r) => s + r.capacity, 0))}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Schedule section */}
        <div style={{ marginBottom: 16, padding: '0 12px' }}>
          {schedule && !showScheduleForm ? (
            <div style={{
              background: '#1E2329',
              border: '1px solid #2B3139',
              borderRadius: 6,
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <CheckCircleOutlined style={{ color: schedule.isActive ? '#0ECB81' : '#848E9C', fontSize: 16 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#E0E0E0' }}>
                    Daily at {parseCronToTime(schedule.cronDaily)} (Strategy: {strategies.find(s => s.id === schedule.strategyId)?.name || schedule.strategyId || 'Unknown'})
                  </div>
                  <div style={{ fontSize: 11, color: schedule.isActive ? '#0ECB81' : '#848E9C' }}>
                    {schedule.isActive ? 'Active' : 'Paused'}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  style={{ color: '#0ECB81' }}
                  onClick={() => {
                    const t = parseCronToTime(schedule.cronDaily);
                    setScheduleTime(dayjs(t, 'HH:mm'));
                    setScheduleActive(schedule.isActive);
                    setSelectedScheduleStrategy(schedule.strategyId ?? null);
                    setShowScheduleForm(true);
                  }}
                />
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  style={{ color: '#F6465D' }}
                  loading={savingSchedule}
                  onClick={handleDeleteSchedule}
                />
              </div>
            </div>
          ) : showScheduleForm ? (
            <div style={{
              background: '#1E2329',
              border: '1px solid #2B3139',
              borderRadius: 6,
              padding: '14px 16px',
            }}>
              {schedule && (
                <div style={{ marginBottom: 12, padding: '8px', background: '#2B3139', borderRadius: 4 }}>
                  <div style={{ fontSize: 12, color: '#848E9C', marginBottom: 4 }}>Current Schedule</div>
                  <div style={{ fontSize: 14, color: '#E0E0E0' }}>
                    Daily at {parseCronToTime(schedule.cronDaily)} (Strategy: {strategies.find(s => s.id === schedule.strategyId)?.name || schedule.strategyId || 'Unknown'})
                  </div>
                </div>
              )}
              <div style={{ fontSize: 12, color: '#848E9C', marginBottom: 10, fontWeight: 600 }}>
                <ClockCircleOutlined style={{ marginRight: 6 }} />
                Configure Daily Schedule
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 11, color: '#848E9C', marginBottom: 4 }}>Time</div>
                  <TimePicker
                    value={scheduleTime}
                    onChange={setScheduleTime}
                    format="HH:mm"
                    size="small"
                    style={{ width: 100 }}
                    minuteStep={5}
                    disabledTime={startWindowDisabledTime}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#848E9C', marginBottom: 4 }}>Active</div>
                  <Switch
                    checked={scheduleActive}
                    onChange={setScheduleActive}
                    size="small"
                    className="grid-advance-switch"
                  />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#848E9C', marginBottom: 4 }}>Strategy</div>
                  <Select
                    value={selectedScheduleStrategy}
                    onChange={setSelectedScheduleStrategy}
                    style={{ width: 150 }}
                    size="small"
                    placeholder="Select strategy"
                    disabled={!!schedule}
                  >
                    {strategies.map((s) => (
                      <Select.Option key={s.id} value={s.id}>
                        {s.name || s.id}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  onClick={() => setShowScheduleForm(false)}
                  style={{ fontSize: 12, height: 28 }}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  loading={savingSchedule}
                  onClick={handleSaveSchedule}
                  style={{
                    fontSize: 12,
                    height: 28,
                    background: '#0ECB81',
                    borderColor: '#0ECB81',
                    color: '#1E2329',
                    fontWeight: 600,
                  }}
                >
                  {schedule ? 'Update' : 'Save'}
                </Button>
              </div>
            </div>
          ) : null}
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '0 12px' }}>
          {!showScheduleForm && (
            <Button
              icon={<ClockCircleOutlined />}
              onClick={() => {
                if (schedule) {
                  // Edit existing schedule
                  const t = parseCronToTime(schedule.cronDaily);
                  setScheduleTime(dayjs(t, 'HH:mm'));
                  setScheduleActive(schedule.isActive);
                  setSelectedScheduleStrategy(schedule.strategyId ?? null);
                } else {
                  // Create new schedule
                  setScheduleTime(dayjs('09:00', 'HH:mm'));
                  setScheduleActive(true);
                  setSelectedScheduleStrategy(strategyId);
                }
                setShowScheduleForm(true);
              }}
              style={{
                borderColor: '#0ECB81',
                color: '#0ECB81',
                fontWeight: 600,
                height: 36,
                padding: '0 20px',
              }}
            >
              {schedule ? 'Configure Schedule' : 'Config Schedule'}
            </Button>
          )}
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            loading={placingGrid}
            disabled={currentNav !== null && placeGridSummary.reduce((s, r) => s + r.intendedBuy, 0) > currentNav}
            onClick={() => executePlaceGrid()}
            style={{
              background: '#F0B90B',
              borderColor: '#F0B90B',
              color: '#1E2329',
              fontWeight: 600,
              height: 36,
              padding: '0 20px',
            }}
          >
            Place Now
          </Button>
        </div>
      </Modal>

      <FirstAllocationModal
        open={allocationOpen}
        plan={allocationPlan}
        placing={placingGrid}
        onCancel={() => setAllocationOpen(false)}
        onConfirm={(allocations) => executePlaceGrid(allocations)}
      />
    </div>
  );
};

export default GridTrading;
